import React from 'react';
import { fmt, fmtCount, fmtSigned } from '../../utils/formatters';
import {
  CONDITION_SHORT, OUTCOME_WORDS, RecordCall, WeatherRecord, byDate, callOutcome, dayLabel, outcomeCounts, recordHeadline,
} from '../../utils/weather';
import CallsChart from './CallsChart';
import RecordTally from './RecordTally';
import { NOTE, TD, TD_NUM, TH, TH_NUM, TableToggle } from './weatherUi';

// The record as a scorecard: the headline in figure type, the tally beside the
// expected-against-actual chart, and one table under a toggle that is the
// accessible version of both. An older payload (no `calls`) keeps the old table.

const sentenceCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** An older payload's recent call, read as a call: no range, so the point is both ends. */
function fromRecent(r: any): RecordCall {
  const dollars = r.dollars ?? 0;
  return {
    for_date: r.for_date, made_on: r.made_on, call: r.call, condition: r.condition ?? r.conditions?.[0]?.condition ?? '',
    hit: r.hit ?? null, weather_happened: r.weather_happened ?? null, scored: r.scored ?? true,
    usual: r.usual ?? 0, dollars, dollars_low: r.dollars_low ?? dollars, dollars_high: r.dollars_high ?? dollars, actual: r.actual ?? null,
  };
}

function CallsTable({ calls }: { calls: RecordCall[] }) {
  return (
    <TableToggle className="mt-4">
      <table className="w-full font-sans text-xs">
        <caption className="sr-only">Every weather call, newest first</caption>
        <thead>
          <tr className="border-b border-[#E6E2DE]">
            <th scope="col" className={TH}>Day</th>
            <th scope="col" className={TH}>Call</th>
            <th scope="col" className={TH_NUM}>Usual</th>
            <th scope="col" className={TH_NUM}>Expected</th>
            <th scope="col" className={TH_NUM}>Sold</th>
            <th scope="col" className={TH_NUM}>Against usual</th>
            <th scope="col" className={TH}>Result</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E6E2DE]">
          {byDate(calls).reverse().map((c) => {
            const [a, b] = [Math.min(c.dollars_low, c.dollars_high), Math.max(c.dollars_low, c.dollars_high)];
            return (
              <tr key={`${c.for_date}-${c.condition}`}>
                <td className={TD}>{dayLabel(c.for_date)}</td>
                <td className={TD}>{sentenceCase(c.call)}{CONDITION_SHORT[c.condition] ? `, ${CONDITION_SHORT[c.condition].toLowerCase()}` : ''}</td>
                <td className={TD_NUM}>{fmt(c.usual)}</td>
                <td className={TD_NUM}>{a === b ? fmt(c.usual + a) : `${fmt(c.usual + a)} to ${fmt(c.usual + b)}`}</td>
                <td className={TD_NUM}>{c.actual != null ? fmt(c.actual) : ''}</td>
                <td className={TD_NUM}>{c.actual != null ? fmtSigned(c.actual - c.usual) : ''}</td>
                <td className={`${TD} pl-3`}>{sentenceCase(OUTCOME_WORDS[callOutcome(c)])}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableToggle>
  );
}

export default function RecordCard({ record, dataThrough }: { record: WeatherRecord; dataThrough: string }) {
  const calls = record.calls;
  const fresh = calls ? calls.length === 0 && !record.total : !record.total && !record.recent?.length;
  const tableRows = calls ?? (record.recent || []).map(fromRecent);
  const never = calls ? outcomeCounts(calls).never : 0;
  return (
    <section aria-labelledby="weather-record" className="rounded-[14px] border border-[#E6E2DE] bg-white p-6 shadow-card">
      <h2 id="weather-record" className="font-serif text-lg text-[#2D2A2E]">The record</h2>
      {fresh ? (
        <div className="mt-4">
          <RecordTally calls={[]} />
          <p className={`${NOTE} mt-3`}>Every call is scored against what the day sold, right or missed, and the last 12 weeks show here.</p>
        </div>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <div data-figure className="font-sans text-[1.5rem] font-bold leading-[1.2] text-[#2D2A2E]">{recordHeadline(record.hits, record.total)}</div>
            <p className={NOTE}>
              calls where the weather came, in the 12 weeks to {dayLabel(dataThrough)}.
              {never > 0 && ` Plus ${fmtCount(never)} where it never came.`}
            </p>
          </div>
          {record.by_condition.length > 1 && (
            <ul className={`${NOTE} mt-1 flex flex-wrap gap-x-4`}>
              {record.by_condition.map((r) => (
                <li key={r.condition} className="tabular-nums">{CONDITION_SHORT[r.condition] ?? r.condition}: {recordHeadline(r.hits, r.total).toLowerCase()}</li>
              ))}
            </ul>
          )}
          {calls && calls.length > 0 && (
            <div className="mt-5 grid gap-6 md:grid-cols-[minmax(0,15rem)_minmax(0,1fr)] md:gap-8">
              <RecordTally calls={calls} />
              <CallsChart calls={calls} />
            </div>
          )}
          {tableRows.length > 0 && <CallsTable calls={tableRows} />}
        </>
      )}
    </section>
  );
}
